import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/use-auth";
import { DashboardBanner } from "@/components/dashboard-banner";
import { checkAdminSettings, SettingsCheckResult } from "@/services/settings-check-services";
import { useCommonTranslation } from "@/i18n/hooks";
import { Text } from "@/components/tailwind/text";
import { Heading } from "@/components/tailwind/heading";
import { UpcomingBirthdaysWidget } from "@/components/dashboard/upcoming-birthdays-widget";
import { MemberStatisticsWidget } from "@/components/dashboard/member-statistics-widget";
import { PendingConfirmationsWidget } from "@/components/dashboard/pending-confirmations-widget";
import { useUserSettingsStore } from "@/stores/user-settings";
import { useToast } from "@/hooks/use-toast";
import { getMyAccount } from "@/services/my-account-services";
import { getSystemEmailStatus } from "@/services/system-email-services";
import log from '@/lib/logger'

export default function DashboardPage() {
    const { t } = useCommonTranslation();
    const { token, hasRole, openChangeEmailDialog } = useAuth();
    const navigate = useNavigate();
    const [settingsCheck, setSettingsCheck] = useState<SettingsCheckResult | null>(null);
    const [isCheckingSettings, setIsCheckingSettings] = useState(false);
    const [showEmailWarning, setShowEmailWarning] = useState(false);
    const isAdmin = hasRole("ADMIN");
    const consumePendingToasts = useUserSettingsStore((state) => state.consumePendingToasts);
    const { toast } = useToast();

    useEffect(() => {
        if (isAdmin && token) {
            checkSettings();
        }
    }, [isAdmin, token]);

    useEffect(() => {
        if (token) {
            checkEmailStatus();
        }
    }, [token]);

    // Consume pending toasts on dashboard load
    useEffect(() => {
        const pendingToasts = consumePendingToasts();
        pendingToasts.forEach((toastData) => {
            toast({
                title: toastData.title,
                description: toastData.description,
                variant: toastData.variant,
                duration: toastData.duration,
            });
        });
    }, [consumePendingToasts, toast]);

    async function checkSettings() {
        if (!token) return;
        
        setIsCheckingSettings(true);
        try {
            const result = await checkAdminSettings(token);
            setSettingsCheck(result);
        } catch (error) {
            log.error("Failed to check admin settings:", error);
        } finally {
            setIsCheckingSettings(false);
        }
    }

    async function checkEmailStatus() {
        if (!token) return;
        try {
            const [emailStatus, account] = await Promise.all([
                getSystemEmailStatus(),
                getMyAccount(token),
            ]);
            setShowEmailWarning(emailStatus.configured && !account.email);
        } catch (error) {
            log.error("Failed to check email status:", error);
        }
    }

    function getMissingSettingsDetails() {
        if (!settingsCheck || settingsCheck.isComplete) return [];
        
        const missing = [];
        if (!settingsCheck.hasOrganizationDetails) {
            missing.push(t("dashboard.missingSettings.organizationDetails"));
        }
        if (!settingsCheck.hasActivePricingScheme) {
            missing.push(t("dashboard.missingSettings.pricingScheme"));
        }
        if (!settingsCheck.hasMileageRate) {
            missing.push(t("dashboard.missingSettings.mileageRate"));
        }
        if (!settingsCheck.hasServices) {
            missing.push(t("dashboard.missingSettings.services"));
        }
        if (!settingsCheck.hasSubmissionChannels) {
            missing.push(t("dashboard.missingSettings.submissionChannels"));
        }
        if (!settingsCheck.hasMembershipFeeTypes) {
            missing.push(t("dashboard.missingSettings.membershipFeeTypes"));
        }
        
        return missing;
    }

    return (
        <div className="space-y-6">
            <div>
                <Heading level={1} data-testid="dashboard-title">{t("dashboard.title")}</Heading>
                <Text className="text-gray-600 dark:text-gray-400 mt-2">
                    {t("dashboard.welcome")}
                </Text>
            </div>

            {isAdmin && settingsCheck && !settingsCheck.isComplete && !isCheckingSettings && (
                <DashboardBanner
                    variant="warning"
                    title={t("dashboard.missingSettings.title", { count: settingsCheck.missingCount })}
                    description={t("dashboard.missingSettings.description")}
                >
                    <ul className="mt-2 list-disc list-inside text-sm text-amber-700 dark:text-amber-300 space-y-1">
                        {getMissingSettingsDetails().map((item, index) => {
                            const getNavigationHandler = (item: string) => {
                                if (item === t("dashboard.missingSettings.services")) {
                                    return { handler: () => navigate("/admin/services"), testId: "dashboard-warning-link-services" };
                                } else if (item === t("dashboard.missingSettings.organizationDetails")) {
                                    return { 
                                        handler: () => {
                                            navigate("/admin/settings");
                                            setTimeout(() => {
                                                const element = document.getElementById("organization");
                                                if (element) {
                                                    element.scrollIntoView({ behavior: "smooth", block: "start" });
                                                }
                                            }, 100);
                                        }, 
                                        testId: "dashboard-warning-link-organization"
                                    };
                                } else if (item === t("dashboard.missingSettings.pricingScheme")) {
                                    return { 
                                        handler: () => {
                                            navigate("/admin/settings");
                                            setTimeout(() => {
                                                const element = document.getElementById("pricing");
                                                if (element) {
                                                    element.scrollIntoView({ behavior: "smooth", block: "start" });
                                                }
                                            }, 100);
                                        }, 
                                        testId: "dashboard-warning-link-pricing"
                                    };
                                } else if (item === t("dashboard.missingSettings.mileageRate")) {
                                    return { 
                                        handler: () => {
                                            navigate("/admin/settings");
                                            setTimeout(() => {
                                                const element = document.getElementById("mileage");
                                                if (element) {
                                                    element.scrollIntoView({ behavior: "smooth", block: "start" });
                                                }
                                            }, 100);
                                        }, 
                                        testId: "dashboard-warning-link-mileage"
                                    };
                                } else if (item === t("dashboard.missingSettings.submissionChannels")) {
                                    return { 
                                        handler: () => {
                                            navigate("/admin/settings");
                                            setTimeout(() => {
                                                const element = document.getElementById("submission");
                                                if (element) {
                                                    element.scrollIntoView({ behavior: "smooth", block: "start" });
                                                }
                                            }, 100);
                                        }, 
                                        testId: "dashboard-warning-link-submission"
                                    };
                                } else if (item === t("dashboard.missingSettings.membershipFeeTypes")) {
                                    return { 
                                        handler: () => {
                                            navigate("/admin/settings");
                                            setTimeout(() => {
                                                const element = document.getElementById("membership-fees");
                                                if (element) {
                                                    element.scrollIntoView({ behavior: "smooth", block: "start" });
                                                }
                                            }, 100);
                                        }, 
                                        testId: "dashboard-warning-link-membership-fees"
                                    };
                                }
                                return null;
                            };

                            const navigationInfo = getNavigationHandler(item);

                            if (navigationInfo) {
                                return (
                                    <li key={index}>
                                        <button 
                                            onClick={navigationInfo.handler}
                                            className="hover:underline cursor-pointer text-amber-700 dark:text-amber-300"
                                            data-testid={navigationInfo.testId}
                                        >
                                            {item}
                                        </button>
                                    </li>
                                );
                            }
                            
                            return <li key={index}>{item}</li>;
                        })}
                    </ul>
                </DashboardBanner>
            )}

            {showEmailWarning && (
                <DashboardBanner
                    variant="info"
                    title={t("dashboard.emailWarning.title")}
                    description={t("dashboard.emailWarning.description")}
                    action={{
                        label: t("dashboard.emailWarning.action"),
                        onClick: () => openChangeEmailDialog(),
                    }}
                />
            )}

            {/* Birthday Widget - Volle Breite */}
            <UpcomingBirthdaysWidget />

            {/* Dashboard Widgets Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <MemberStatisticsWidget />
                {isAdmin && <PendingConfirmationsWidget />}
            </div>
        </div>
    );
}